import * as React from "react";

const { Component } = React;

interface HomeState {
    context: string;
    appInfo: any;
    loading: boolean;
    calendars: Array<any>;
    selectedCalendar: any;
    showCalendarModal: boolean;
}

import { Box, Button } from "grommet";
import { Anchor, Calendar, CheckBox, Grid, Layer, Skeleton, Stack, Text, TextInput } from "grommet/components";
import CalendarDetails from "../calendar/CalendarDetails";
import * as CalendarStore from "../calendar/CalendarStore";
import CalendarView from "../calendar/CalendarView";
import { useDebounceCallback } from "usehooks-ts";

let calendarRenderFunc = (calendar) => <span>{calendar.name}</span>;
let calendarKeyFunc = (calendar) => calendar.id;
let calendarFilters = [{ key: "name", name: "Name" }];

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { endOfMonth, format, parseISO, startOfMonth } from "date-fns";
import randomColor from "randomcolor";
import { useUser } from "../account/AccountStore";
import ScrollContainer from "../components/ScrollContainer";
import { IdentityUser, SearchUsers } from "../organizations/IdentityAPI";
import SkeletonWrapper from "../components/SkeletonWrapper";
import { ca } from "date-fns/locale";
import { uniq, uniqBy } from "lodash";
import StyledModal from "../components/StyledModal";

const Home = () => {
    const [loading, setLoading] = React.useState(true);
    const [calendars, setCalendars] = React.useState([] as CalendarStore.CalendarViewModel[]);
    const [savedCalendars, setSavedCalendars] = React.useState([] as CalendarStore.CalendarViewModel[]);
    const [selectedDay, setSelectedDay] = React.useState();
    const [currentCalendar, setSelectedCalendar] = React.useState({} as CalendarStore.CalendarViewModel);
    const [showCalendarModal, setShowCalendarModal] = React.useState(false);
    const [clickedEventID, setClickedEventID] = React.useState(0);

    const [selecteduser, setUserCalendars] = React.useState([] as string[]);
    const [selectedCalendars, setSelectedCalendars] = React.useState([] as string[]);
    const [viewMode, setViewMode] = React.useState("month");
    const [leftBar, setLeftBar] = React.useState(true);
    const [showCalendarEdit, setShowCalendarEdit] = React.useState(false);
    const [showSelected, setShowSelected] = React.useState(true);

    const [calendarType, setCalendarType] = React.useState(0);

    const [skip, setSkip] = React.useState(0);
    const [limit, setLimit] = React.useState(10);
    const [loadingState, setLoadingState] = React.useState("" as "" | "loading user" | "loading calendar" | "loading external calendar");

    React.useEffect(() => {
        getCalendars();
    }, []);

    React.useEffect(() => {
        setSelectedCalendar(calendars.find((calendar) => calendar.id == currentCalendar.id) || ({} as CalendarStore.CalendarViewModel));
    }, [calendars]);

    React.useEffect(() => {
        getCalendars();
    }, [calendarType]);

    React.useEffect(() => {
        if (selectedCalendars.length != 0) {
            localStorage.setItem("selectedCalendars", JSON.stringify(selectedCalendars));
        }
    }, [selectedCalendars]);

    const loadSavedCalendars = (): Promise<CalendarStore.CalendarViewModel[]> => {
        return new Promise((resolve, reject) => {
            const savedSelectedCalendarsJSON = localStorage.getItem("selectedCalendars");
            if (savedSelectedCalendarsJSON) {
                let savedSelectedCalendars = JSON.parse(savedSelectedCalendarsJSON);
                setSelectedCalendars(savedSelectedCalendars);
                setLoadingState("loading external calendar");
                CalendarStore.ResolveCalendars(savedSelectedCalendars, format(startOfMonth(referenceDate), "yyy/MM/dd"), format(endOfMonth(referenceDate), "yyy/MM/dd")).then((response) => {
                    response.data.forEach((calendar) => {
                        if (calendar.color == null) {
                            calendar.color = randomColor();
                        }
                    });
                    setLoadingState("");
                    resolve(response.data);
                });
            } else {
                setLoadingState("");
                resolve([]);
            }
        });
    };

    const getCalendars = () => {
        return Promise.all([CalendarStore.ListCalendars(calendarType, skip, limit).then((response) => response.data.result), loadSavedCalendars()]).then((responses) => {
            setCalendars(uniqBy(responses.flat(), (c) => c.id));
            setSelectedCalendar({} as CalendarStore.CalendarViewModel);
            setLoading(false);
        });
    };

    const handleCalendarChange = () => {
        getCalendars();
    };

    const clearCalendar = () => {
        setSelectedCalendar({} as CalendarStore.CalendarViewModel);
        setShowCalendarEdit(false);
        getCalendars();
    };

    const loadMore = () => {
        setSkip(skip + limit);
        CalendarStore.ListCalendars(calendarType, skip + limit, limit).then((response) => {
            setCalendars([...calendars, ...response.data.result]);
        });
    };

    const clickedEvent = (id) => {
        setClickedEventID(id);
        console.log(id);
    };

    const onSelect = (value) => {
        if (value == selectedDay) {
            setSelectedDay(undefined);
        } else {
            setSelectedDay(value);
        }
    };

    const updateSelectedCalendars = (id, checked) => {
        if (checked == true) {
            setSelectedCalendars([...selectedCalendars, id]);
        } else {
            setSelectedCalendars(selectedCalendars.filter((calendar) => calendar != id));
        }
    };

    const [query, setQuery] = React.useState("");
    const [users, setUsers] = React.useState([] as IdentityUser[]);
    const user = useUser();

    React.useEffect(() => {
        searchUsers(query);
    }, [query]);

    const searchUsers = useDebounceCallback((query) => {
        if (query.length > 2) {
            setLoadingState("loading user");
            SearchUsers(user.organization.id, query).then((response) => {
                setUsers(response);
                setLoadingState("");
            });
        }
    }, 300);

    const selectUser = (selectedUser) => {
        setShowSelected(false);
        setLoadingState("loading calendar");
        CalendarStore.GetCalendarsForUser(selectedUser.id).then((userCalendarsResponse) => {
            if (selectedUser.email) {
                if (user.hasPermission("events:externalCalendars.read") || user.getClaim("email") == selectedUser.email) {
                    setLoadingState("loading external calendar");
                    CalendarStore.GetMicrosoftCalendarsForUser(selectedUser.email, format(startOfMonth(referenceDate), "yyy/MM/dd"), format(endOfMonth(referenceDate), "yyy/MM/dd")).then(
                        (microsoftCalendarsResult) => {
                            if (microsoftCalendarsResult.data.value) {
                                microsoftCalendarsResult.data.value.color = randomColor();
                            }
                            setCalendars([...userCalendarsResponse.data.result, microsoftCalendarsResult.data.value]);
                            setLoadingState("");
                        }
                    );
                }
            }
            setCalendars(userCalendarsResponse.data.result);
        });
    };
    const [referenceDate, setReferenceDate] = React.useState(new Date());

    const editCalendar = (calendar) => {
        setSelectedCalendar(calendar);
        setShowCalendarEdit(true);
    };

    const createCalendar = () => {
        setSelectedCalendar({} as CalendarStore.CalendarViewModel);
        setShowCalendarEdit(true);
    };

    return (
        <Box pad={{ bottom: "small" }}>
            <SkeletonWrapper loading={loading}>
                <Box pad="small" gap="xsmall">
                    <Box pad="small" round="xsmall" align="center" justify="between" direction="row" background="background-back">
                        <Box direction="row">
                            <Button icon={<FontAwesomeIcon icon={["fad", "bars"]}></FontAwesomeIcon>} onClick={() => setLeftBar(!leftBar)}></Button>
                            <Grid columns={{ count: 4, size: "auto" }} gap="xxsmall">
                                <Button label="Month" primary={viewMode == "month" ? true : false} style={{ border: "none" }} onClick={() => setViewMode("month")}></Button>
                                {/* <Button label="Week" primary={viewMode == "week" ? true : false} style={{ border: "none" }} onClick={() => setViewMode("week")}></Button> */}
                                <Button label="Day" primary={viewMode == "day" ? true : false} style={{ border: "none" }} onClick={() => setViewMode("day")}></Button>
                            </Grid>
                        </Box>
                        <Box justify="end">
                            <Anchor
                                href={`/account/signin`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    window.location = `/account/signin?select=true&redirectUrl=${encodeURIComponent(window.location.pathname)}` as any;
                                }}
                                label={user?.organization?.name || "Sign In"}
                            ></Anchor>
                        </Box>
                    </Box>

                    <Grid fill columns={leftBar ? ["15%", "85%"] : ["100%"]} gap={{ row: "xsmall", column: "small" }}>
                        {leftBar && (
                            <Box
                                background="background-back"
                                height="87vh"
                                width="medium"
                                round="xsmall"
                                pad="small"
                                gap="small"
                                animation={[
                                    { type: "fadeIn", duration: 500 },
                                    { type: "slideRight", size: "xlarge", duration: 200 },
                                ]}
                            >
                                <Box align="center" pad={{ bottom: "small" }} border={{ side: "bottom" }}>
                                    <Calendar date={selectedDay} daysOfWeek onSelect={onSelect} size="small" bounds={["2020-09-08", "2025-12-13"]} />
                                </Box>

                                {/* <Button label="Edit" justify="start" style={{  border: "none", paddingLeft: "10px" }} icon={<FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>}></Button> */}
                                <SkeletonWrapper loading={loadingState == "loading calendar"}>
                                    <Box gap="small">
                                        <Text weight="bold">My Calendars</Text>
                                        {showSelected
                                            ? calendars
                                                  .filter((sC) => selectedCalendars.includes(sC.id))
                                                  .map((calendar) => (
                                                      <Box key={calendar.id}>
                                                          <Box direction="row" gap="small">
                                                              <CheckBox
                                                                  label={calendar.name}
                                                                  checked={selectedCalendars.includes(calendar.id)}
                                                                  onChange={(e) => updateSelectedCalendars(calendar.id, e.target.checked)}
                                                              />
                                                              <Box onClick={() => editCalendar(calendar)} hoverIndicator>
                                                                  <FontAwesomeIcon icon={["fad", "pen"]}></FontAwesomeIcon>
                                                              </Box>
                                                          </Box>
                                                      </Box>
                                                  ))
                                            : calendars.map((calendar) => (
                                                  <Box key={calendar.id}>
                                                      <Box direction="row" gap="small">
                                                          <CheckBox
                                                              label={calendar.name}
                                                              checked={selectedCalendars.includes(calendar.id)}
                                                              onChange={(e) => updateSelectedCalendars(calendar.id, e.target.checked)}
                                                          />
                                                          <Box onClick={() => editCalendar(calendar)} hoverIndicator>
                                                              <FontAwesomeIcon icon={["fad", "pen"]}></FontAwesomeIcon>
                                                          </Box>
                                                      </Box>
                                                  </Box>
                                              ))}
                                        <SkeletonWrapper loading={loadingState == "loading external calendar"}>
                                            <></>
                                        </SkeletonWrapper>
                                        {user.hasPermission("events:calendars.create") && <Button label="Create Calendar" onClick={createCalendar}></Button>}
                                    </Box>
                                </SkeletonWrapper>
                                <Anchor onClick={() => setShowSelected(!showSelected)} label={showSelected ? "Show All" : "Show Selected"} color="brand"></Anchor>
                                <TextInput placeholder="Search Users" value={query} onChange={(e) => setQuery(e.target.value)}></TextInput>
                                <SkeletonWrapper loading={loadingState == "loading user"}>
                                    <>
                                        {users.map((user) => (
                                            <Anchor onClick={() => selectUser(user)} label={user.fullName} key={user.id}></Anchor>
                                        ))}
                                    </>
                                </SkeletonWrapper>
                            </Box>
                        )}

                        <CalendarView
                            calendars={calendars.filter((calendar) => selectedCalendars.includes(calendar.id))}
                            leftBarDay={selectedDay}
                            month={referenceDate}
                            viewMode={viewMode}
                            onSelectDay={onSelect}
                            onReference={(d) => setReferenceDate(parseISO(d))}
                            onChange={() => handleCalendarChange()}
                        ></CalendarView>
                    </Grid>
                    {/* <Button onClick={() => setShowCalendarModal(true)} label="Manage Calendars"></Button> */}
                </Box>
            </SkeletonWrapper>
            {showCalendarEdit && (
                <StyledModal isOpen={showCalendarEdit} setIsOpen={setShowCalendarEdit} full={false} position="center">
                    <Stack anchor="top-right">
                        <Box width="large">
                            <CalendarDetails calendar={currentCalendar} onChange={handleCalendarChange} onDone={() => setShowCalendarEdit(false)} clear={clearCalendar}></CalendarDetails>
                        </Box>

                        <Box pad="small" margin="xsmall" round="xsmall" onClick={() => setShowCalendarEdit(false)} hoverIndicator={{ color: "status-critical" }}>
                            <FontAwesomeIcon icon={["fad", "x"]} color="background-back"></FontAwesomeIcon>
                        </Box>
                    </Stack>
                </StyledModal>
            )}
        </Box>
    );
};

export default Home;
